<template>
    <LegendItem description="Actief rooster: rooster is gepubliceerd" isHeader>
        <PlanbordHeaderCell
            :index="1"
            :day="dayPublished"
            :roster="roster"
            :viewOptions="viewOptions"
        />
    </LegendItem>
    <LegendItem v-if="globals.rosterHasDeriveRightsUntilEnabled" description="Actief, nog niet definitief rooster: rooster is gepubliceerd zonder rechten" isHeader>
        <PlanbordHeaderCell
            :index="1"
            :day="dayPublished"
            :roster="rosterPublishedWithoutRights"
            :viewOptions="viewOptions"
        />
    </LegendItem>
    <LegendItem description="In ontwikkeling: rooster is voorgesteld" isHeader>
        <PlanbordHeaderCell
            :index="1"
            :day="dayProposed"
            :roster="rosterProposed"
            :viewOptions="viewOptions"
        />
    </LegendItem>
    <LegendItem description="In ontwikkeling: rooster is voorgesteld en wordt getoond" isHeader>
        <PlanbordHeaderCell
            :index="1"
            :day="dayProposed"
            :roster="rosterWithUnpublishedPartVisible"
            :viewOptions="viewOptions"
        />
    </LegendItem>
    <LegendItem description="In ontwikkeling: rooster is in bewerking of in stempelfase" isHeader>
        <PlanbordHeaderCell
            :index="1"
            :day="day"
            :roster="roster"
            :viewOptions="viewOptions"
        />
    </LegendItem>
    <LegendItem description="In ontwikkeling: rooster is in bewerking of in stempelfase en wordt getoond" isHeader>
        <PlanbordHeaderCell
            :index="1"
            :day="day"
            :roster="rosterWithUnpublishedPartVisible"
            :viewOptions="viewOptions"
        />
    </LegendItem>
    <LegendItem description="Inactief rooster: rooster heeft een einddatum en is daarna niet meer actief" isHeader>
        <PlanbordHeaderCell
            :index="1"
            :day="dayAfterRosterEnd"
            :roster="roster"
            :viewOptions="viewOptions"
        />
    </LegendItem>
</template>

<script lang="ts">
    import PlanbordHeaderCell from '@/components/Planbord/PlanbordHeaderCell.vue';
    import {RosterData, RosterDay, ViewOptions} from '@/types/types';
    import Dates from '@cquential/date-utils';
    import {Globals, RosterRankSet} from '@cquential/api';
    import LegendItem from '@/components/modals/Legend/LegendItem.vue';
    import {DEFAULT_BEGIN_DATE, DEFAULT_END_DATE} from '@/util/Defaults';
    import {Component, Vue, Prop} from 'vue-facing-decorator';

    @Component({
        components: {
            PlanbordHeaderCell,
            LegendItem,
        },
    })
    export default class HeaderLegendItems extends Vue {
        @Prop({required: true}) public viewOptions!: ViewOptions;
        @Prop({required: true}) public globals!: Globals;

        private get startDate(): Date {
            return Dates.Utils.startOfWeek(Dates.Utils.today());
        }

        private get day(): RosterDay {
            return {
                ended: false,
                published: false,
                proposed: false,
                holiday: false,
                dayName: 'Maandag',
                dayAbbreviation: 'ma',
                formattedDateOnly: Dates.Formatter.formatDateWithoutYear(this.startDate),
                formattedForComputers: Dates.Formatter.formatDateComputerFriendly(this.startDate),
                formattedDateIso: Dates.Formatter.formatDateISO(this.startDate),
                weekend: false,
                date: this.startDate,
            };
        }

        private get dayPublished(): RosterDay {
            return {...this.day, published: true};
        }

        private get dayProposed(): RosterDay {
            return {...this.day, proposed: true};
        }

        private get dayAfterRosterEnd(): RosterDay {
            return {...this.day, ended: true};
        }

        private get rosterRankSet(): RosterRankSet {
            return {
                rosterId: 1,
                beginDate: new Date(DEFAULT_BEGIN_DATE),
                endDate: new Date(DEFAULT_END_DATE),
                titles: [],
                resourceRosterRanks: [],
            };
        }

        private get roster(): RosterData {
            return {
                rosterResources: [],
                resourcesFromOtherRosters: [],
                externalResources: [],
                relatedResources: [],
                beginDate: Dates.Utils.addDays(this.startDate, -90),
                endDate: Dates.Utils.addDays(this.startDate, 4),
                proposedUntil: Dates.Utils.addDays(this.startDate, 1),
                deriveRightsUntil: this.startDate,
                publishedUntil: this.startDate,
                days: [],
                rosterId: 1,
                rosterRankSet: this.rosterRankSet,
                companies: {},
                organisationId: 'Organisatie',
                timeTypes: {},
                holidays: [],
                unpublishedPartVisibleUntil: null,
                wishInputClosedUntil: null,
                wishInputEnabled: false,
                openShifts: {},
                usesMarketplaceFunctions: false,
            };
        }

        private get rosterPublishedWithoutRights(): RosterData {
            return {...this.roster, deriveRightsUntil: Dates.Utils.addDays(this.startDate, -1)};
        }

        private get rosterProposed(): RosterData {
            return {...this.roster, proposedUntil: this.startDate};
        }

        private get rosterWithUnpublishedPartVisible(): RosterData {
            return {
                ...this.roster,
                publishedUntil: Dates.Utils.addDays(this.startDate, -1),
                unpublishedPartVisibleUntil: this.startDate,
            };
        }
    }
</script>
